/**
 * The device's own cryptographic identity.
 *
 * ECDSA over P-256 because it is the one asymmetric signing algorithm every
 * browser's WebCrypto implements, and because `Devices::RequestSignature` on the
 * server verifies exactly that curve and nothing else.
 *
 * `extractable: false` is the point of the whole exercise. A key generated this
 * way can be handed to `subtle.sign` and stored in IndexedDB, but its bytes
 * never exist in JavaScript and `exportKey` refuses — so unlike a token, an
 * attacker who can run code on the page can use the key while they are there
 * and cannot take it with them.
 */
export const SIGNING_ALGORITHM = { name: "ECDSA", namedCurve: "P-256" } as const
export const SIGNING_PARAMS = { name: "ECDSA", hash: "SHA-256" } as const

export interface GeneratedKeyPair {
  privateKey: CryptoKey
  /** SPKI DER, base64 — what the server stores and verifies against. */
  publicKeySpki: string
}

export async function generateSigningKeyPair(): Promise<GeneratedKeyPair> {
  const pair = await crypto.subtle.generateKey(SIGNING_ALGORITHM, false, ["sign", "verify"])

  return {
    privateKey: pair.privateKey,
    publicKeySpki: encodeBase64(await crypto.subtle.exportKey("spki", pair.publicKey))
  }
}

/**
 * Confirms what the flag claims. Enrollment reports this to the operator rather
 * than assuming it: a browser that quietly ignored `extractable: false` would
 * leave a copyable key on a station computer and nothing would say so.
 */
export async function isNonExtractable(key: CryptoKey): Promise<boolean> {
  if (key.extractable) return false

  try {
    await crypto.subtle.exportKey("pkcs8", key)
    return false
  } catch {
    return true
  }
}

/** The raw r‖s pair WebCrypto produces, base64 for the header. */
export async function sign(privateKey: CryptoKey, message: string): Promise<string> {
  const signature = await crypto.subtle.sign(SIGNING_PARAMS, privateKey, encodeUtf8(message))
  return encodeBase64(signature)
}

/**
 * The offline-login secret, imported as a non-extractable HMAC key the moment
 * it arrives so the only copy on this machine is one that cannot be read back.
 */
export async function importOfflineSecret(base64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", decodeBase64(base64), { name: "HMAC", hash: "SHA-256" }, false, ["sign"])
}

/** HMAC-SHA256 over a canonical string, as raw bytes for whoever has to encode them. */
export async function signHmac(key: CryptoKey, message: string): Promise<Uint8Array<ArrayBuffer>> {
  const signature = await crypto.subtle.sign("HMAC", key, encodeUtf8(message))
  return new Uint8Array(signature) as Uint8Array<ArrayBuffer>
}

/**
 * The server's snapshot-signing public key. Extractable, because it is public
 * and the device has to be able to hold it across restarts in IndexedDB; nothing
 * is protected by keeping a public key secret.
 */
export async function importVerificationKey(spkiBase64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("spki", decodeBase64(spkiBase64), SIGNING_ALGORITHM, true, ["verify"])
}

/** Verifies a raw r‖s signature, the form the server converts its DER output into. */
export async function verifySignature(publicKey: CryptoKey, signatureBase64: string, message: string): Promise<boolean> {
  return crypto.subtle.verify(SIGNING_PARAMS, publicKey, decodeBase64(signatureBase64), encodeUtf8(message))
}

// The `<ArrayBuffer>` argument is not decoration. TypeScript now models
// `Uint8Array` over its backing buffer, and the default admits
// `SharedArrayBuffer` — which WebCrypto's `BufferSource` does not accept, so
// every `sign`/`digest` call fails to typecheck without it.
export function encodeUtf8(value: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(value) as Uint8Array<ArrayBuffer>
}

export function encodeBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

export function decodeBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return bytes
}
